import { SearchOutlined } from '@ant-design/icons';
import { Table, Modal as MyModal, Input, Row, Col, Input as inputAntd } from 'antd';
import { medicalInstitution } from 'containers/App/selectors';
import { Form } from 'formik-antd';
import { getMedicalInstitution } from 'pages/Register/RegisterFirstStep/thunk';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { RootState, t_cpap_instructions, t_medical_institution } from 'types';
import { setMedical } from '../../pages/Register/slices';

const Wrap = styled.div`
  && {
    .ant-modal-content {
      .ant-btn {
        box-shadow: 0 3px 6px rgb(0 0 0 / 25%);
        border-radius: 5px !important;
      }
    }
    input[name='facility_name'] {
      pointer-events: none;
    }
    .middleFont {
      text-align: center;
    }
  }
`;

type Props = {
  onChangeSearch: (facilityName: string, s: string | undefined) => void;
  type?: number;
  open?: boolean;
  valueSelected?: t_cpap_instructions;
  onVisible?: () => void;
  keySelected?: string;
};

const Modal: FC<Props> = ({ open, onChangeSearch, type, valueSelected, onVisible, keySelected }) => {
  const dispatch = useDispatch();
  const medicalInstitutionList = useSelector(medicalInstitution);
  const { medical } = useSelector((state: RootState) => state.register);
  const [visible, setVisible] = useState<boolean>(false);
  const { Search } = inputAntd;
  const selectedKey = medicalInstitutionList.find((m) => m.i_id === (medical.id || keySelected))?.i_id;
  const [medicalInfo, setMedicalInfo] = useState<t_cpap_instructions | undefined>(valueSelected);
  const [selectionType] = useState<'checkbox' | 'radio'>('checkbox');
  const [selectedRows, setSelectedRows] = useState<string[]>(selectedKey ? [selectedKey] : []);

  useEffect(() => {
    (visible || open) && dispatch(getMedicalInstitution());
  }, [dispatch, visible, open]);

  const handleFilter = (e: { target: HTMLInputElement }) => {
    dispatch(getMedicalInstitution(e.target.value));
  };
  return (
    <Wrap style={{ display: `${type === 1 ? 'none' : 'block'}` }}>
      <Row>
        <Col span={5}>
          <div className="titleInput">
            <span className="standardBoldFont space">医療機関</span>
            <span className="standardFont ※">※</span>
          </div>
        </Col>
        <Col span={13}>
          <div>
            <Form.Item name="facility_name">
              <Search
                onSearch={(value) => {
                  onVisible && onVisible();
                  setVisible(true);
                }}
                value={medicalInfo?.facility_name}
                name="facility_name"
                enterButton="選択"
              />
            </Form.Item>
          </div>
        </Col>
      </Row>
      <Row>
        <Col span={5}>
          <div className="titleInput">
            <span className="standardBoldFont space">営業担当</span>
          </div>
        </Col>
        <Col className="standardFont" span={6}>
          {medicalInfo?.in_charge_of_sales}
        </Col>
      </Row>
      <MyModal
        className="modal"
        width="90%"
        closable={false}
        okText="設定する"
        cancelText="キャンセル"
        title="医療機関の変更"
        visible={open || visible}
        onOk={() => {
          onVisible && onVisible();
          setVisible(false);
          const name = medicalInstitutionList.find((f) => f.i_id === selectedRows[0]);
          if (name && keySelected !== name.i_id) {
            setMedicalInfo(name);
            dispatch(
              setMedical({
                medicalId: name.i_id || '',
                isUpdate: true,
              }),
            );
            onChangeSearch('facility_name', name.facility_name);
            onChangeSearch('in_charge_of_sales', name.in_charge_of_sales);
          }
        }}
        onCancel={() => {
          onVisible && onVisible();
          setVisible(false);
          setSelectedRows([]);
        }}
        bodyStyle={{ textAlign: 'center', backgroundColor: '#EEEEEE', justifyContent: 'center' }}
        cancelButtonProps={{ disabled: !selectedRows }}
        okButtonProps={{ disabled: !selectedRows }}
      >
        <div>
          <div className="middleFont" style={{ textAlign: 'center' }}>
            変更する医療機関を選択し「設定する」ボタンをクリックしてください
          </div>
          <Input
            onChange={(e) => handleFilter(e)}
            style={{ width: '60%', borderRadius: '19px', margin: '15px 0 28px 0' }}
            name="search"
            className="filter"
            allowClear
            placeholder="検索"
            prefix={<SearchOutlined />}
          />
          <div>
            <Table
              scroll={{ x: 0, y: 300 }}
              pagination={false}
              rowKey={(record) => {
                return record.i_id ? record.i_id : '';
              }}
              rowSelection={{
                selectedRowKeys: selectedRows,
                type: selectionType,
                onSelect: (e) => {
                  const value = e.i_id;
                  const keySelected = selectedRows?.find((s) => s === value);
                  if (keySelected) {
                    setMedicalInfo(undefined);
                    setSelectedRows([]);
                  } else {
                    value && setSelectedRows([value]);
                  }
                },
              }}
              columns={columns}
              dataSource={medicalInstitutionList}
            />
          </div>
        </div>
      </MyModal>
    </Wrap>
  );
};

const columns = [
  {
    title: '医療機関名',
    dataIndex: 'facility_name',
    width: '26%',
    render(text: string) {
      return {
        props: {
          style: { textAlign: 'left' },
          className: 'standardFont',
        },
        children: <div>{text}</div>,
      };
    },
  },
  {
    title: '郵便番号',
    dataIndex: 'facility_zip',
    width: '13%',
    render(text: string) {
      return {
        props: {
          style: { background: 'rgba(246, 246, 246, 1)', textAlign: 'left' },
          className: 'standardFont',
        },
        children: <div>{text}</div>,
      };
    },
  },
  {
    title: '住所',
    dataIndex: 'facility_address1',
    render(text: string, record: t_medical_institution) {
      return {
        props: {
          style: { textAlign: 'left' },
          className: 'standardFont',
        },
        children: <div>{`${record.facility_address1 || ''} ${record.facility_address2 || ''}`}</div>,
      };
    },
  },
  {
    title: 'TEL',
    dataIndex: 'facility_tel',
    width: '20%',
    render(text: string) {
      return {
        props: {
          style: { background: 'rgba(246, 246, 246, 1)', textAlign: 'left' },
          className: 'standardFont',
        },
        children: <div>{text}</div>,
      };
    },
  },
];

export default Modal;
